import { useState } from "react";
import { motion } from "framer-motion";
import { Crown, Users, Globe, TrendingUp, ArrowRight, ShieldCheck, Info } from "lucide-react";
import { fmtINR } from "../lib/api";
import { Reveal } from "./Shared";

/* Display-only plan ladder for the Super Admin Panel (educational section). */
const SA_PLANS = [
  { partners: 3, price: 6000, name: "Starter" },
  { partners: 5, price: 9000, name: "Growth" },
  { partners: 10, price: 16000, name: "Professional" },
  { partners: 20, price: 28000, name: "Business" },
  { partners: 30, price: 39000, name: "Enterprise" },
];

/* Illustrative per-partner billing examples (existing partner pricing — unchanged). */
const EXAMPLES = [
  { name: "Partner 1", sites: 30, amount: 3000 },
  { name: "Partner 2", sites: 20, amount: 3000 },
  { name: "Partner 3", sites: 50, amount: 3500 },
];

function AnimNum({ value }) {
  return (
    <motion.span key={value} initial={{ opacity: 0.4, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      {fmtINR(value)}
    </motion.span>
  );
}

export default function SuperAdminSection() {
  const [partners, setPartners] = useState(5);
  const plan = SA_PLANS.find((p) => partners <= p.partners) || SA_PLANS[SA_PLANS.length - 1];
  const perPartner = 3000; // illustrative average partner website billing
  const network = partners * perPartner;
  const margin = network - plan.price;

  return (
    <section id="super-admin" className="py-24 relative overflow-hidden" data-testid="super-admin-section">
      <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full opacity-20 blur-3xl animate-blob" style={{ background: "var(--p1)" }} />
      <div className="absolute -bottom-40 -right-24 h-96 w-96 rounded-full opacity-15 blur-3xl animate-blob" style={{ background: "var(--p3)", animationDelay: "-7s" }} />

      <div className="max-w-6xl mx-auto px-6 relative">
        <Reveal className="text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card text-[11px] font-extrabold uppercase tracking-widest">
            <Crown className="h-3.5 w-3.5 text-accent" /> Super Admin Panel
          </span>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter mt-5">
            Manage your own <span className="text-gradient">Partner network</span>
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto text-base sm:text-lg">
            A second SaaS business model built into the platform: Super Admins run their own network of Partners
            and build recurring SaaS revenue on top of it.
          </p>
        </Reveal>

        {/* Plan ladder */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mt-14" data-testid="sa-plans">
          {SA_PLANS.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.07}>
              <div data-testid={`sa-plan-${p.name.toLowerCase()}`}
                className={`relative rounded-2xl p-6 text-center h-full transition-all duration-300 hover:-translate-y-1.5 ${i === 2 ? "g-border glow" : "glass-card hover:glow-sm"}`}>
                {i === 2 && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full gradient-bg text-white text-[10px] font-extrabold tracking-widest uppercase glow-sm">Popular</span>}
                <div className={`h-10 w-10 mx-auto rounded-xl flex items-center justify-center ${i === 2 ? "gradient-bg text-white" : "bg-muted"}`}>
                  <Users className="h-4.5 w-4.5" />
                </div>
                <p className="font-display font-extrabold text-3xl tracking-tighter mt-4">{p.partners}</p>
                <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest">Partners</p>
                <p className="font-display font-extrabold text-xl mt-3 text-gradient">{fmtINR(p.price)}</p>
                <p className="text-[11px] text-muted-foreground">/month</p>
                <p className="text-[10px] font-extrabold uppercase tracking-widest mt-3 opacity-60">{p.name}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Revenue model visual */}
        <Reveal className="mt-16">
          <div className="glass-card rounded-2xl p-8 sm:p-10" data-testid="sa-revenue-model">
            <p className="overline-label">How the revenue model works</p>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Each Partner independently manages their own Business Owner websites, and each Partner's website billing
              stays exactly as the existing per-partner pricing — never combined.
            </p>
            <div className="grid md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-6 items-center mt-8">
              <div className="space-y-3">
                {EXAMPLES.map((e, i) => (
                  <motion.div key={e.name} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }}
                    className="flex items-center justify-between border border-border rounded-xl px-5 py-4 bg-background/60" data-testid={`sa-example-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full gradient-bg text-white flex items-center justify-center font-display font-extrabold text-xs">{e.name.split(" ")[1]}</div>
                      <div><p className="text-sm font-extrabold">{e.name}</p><p className="text-xs text-muted-foreground">{e.sites} websites</p></div>
                    </div>
                    <p className="font-display font-extrabold text-gradient">{fmtINR(e.amount)}<span className="text-[10px] text-muted-foreground font-sans font-bold">/mo</span></p>
                  </motion.div>
                ))}
              </div>
              <div className="hidden md:flex flex-col items-center gap-1 text-muted-foreground">
                <ArrowRight className="h-5 w-5" />
                <span className="text-[10px] font-extrabold uppercase tracking-widest">independent</span>
                <ArrowRight className="h-5 w-5" />
              </div>
              <div className="g-border rounded-2xl p-7 text-center glow-sm">
                <ShieldCheck className="h-6 w-6 mx-auto text-accent" />
                <p className="font-display font-extrabold text-lg mt-3">Your Super Admin panel</p>
                <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                  Partners keep their own billing. Your panel scales as your Partner network grows.
                </p>
                <p className="font-display font-extrabold text-2xl mt-4 text-gradient">{fmtINR(EXAMPLES.reduce((s, e) => s + e.amount, 0))}<span className="text-xs text-muted-foreground font-sans">/mo network billing</span></p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Interactive calculator */}
        <Reveal className="mt-10">
          <div className="g-border rounded-2xl p-8 sm:p-10 glow" data-testid="sa-calculator">
            <div className="flex items-center gap-2">
              <p className="overline-label">Network revenue calculator</p>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-[10px] font-extrabold uppercase tracking-widest">
                <Info className="h-3 w-3" /> Illustrative
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-8 mt-6">
              <div className="flex-1 min-w-64">
                <div className="flex justify-between text-sm font-bold mb-3">
                  <span>Partners in your network</span>
                  <span data-testid="sa-calc-partners" className="font-display font-extrabold text-xl text-gradient">{partners}</span>
                </div>
                <input data-testid="sa-calc-slider" type="range" min="1" max="30" value={partners}
                  onChange={(e) => setPartners(Number(e.target.value))}
                  className="w-full h-2 rounded-full appearance-none cursor-pointer accent-[var(--p2)]" style={{ background: "linear-gradient(90deg, var(--p1), var(--p3))" }} />
                <div className="flex justify-between text-xs text-muted-foreground mt-2"><span>1</span><span>10</span><span>20</span><span>30</span></div>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Matching plan</p>
                <motion.p key={plan.name} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} data-testid="sa-calc-plan"
                  className="font-display font-extrabold text-xl tracking-tight">{plan.name} · up to {plan.partners}</motion.p>
                <p className="font-display font-extrabold text-4xl tracking-tighter text-gradient mt-1" data-testid="sa-calc-cost"><AnimNum value={plan.price} /><span className="text-sm text-muted-foreground font-sans font-bold">/mo</span></p>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
              {[
                { l: "Super Admin plan cost", v: plan.price, t: "sa-calc-plan-cost", icon: Crown },
                { l: "Example partner billing", v: perPartner, t: "sa-calc-per-partner", icon: Globe, suffix: " /partner /mo" },
                { l: "Illustrative network total", v: network, t: "sa-calc-network", icon: Users },
                { l: "Potential remaining margin", v: margin, t: "sa-calc-margin", icon: TrendingUp },
              ].map((c) => (
                <div key={c.l} className="rounded-xl border border-border bg-background/60 p-5" data-testid={c.t}>
                  <c.icon className="h-4 w-4 text-accent" />
                  <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mt-2.5">{c.l}</p>
                  <p className={`font-display font-extrabold text-2xl tracking-tighter mt-1 ${c.v < 0 ? "text-red-500" : "text-gradient"}`}>
                    <AnimNum value={c.v} /><span className="text-[10px] text-muted-foreground font-sans font-bold">{c.suffix || " /mo"}</span>
                  </p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-6 leading-relaxed">
              Illustrative only — partner billing examples use the existing per-partner website pricing (₹3,000 flat for 1–30 sites, ₹70/site for 31–300, ₹50/site above).
              Figures are educational estimates, not a guarantee of earnings.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
